import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	upsertBarrelLayout,
	deleteBarrelLayout,
	reorderBarrelLayout,
	type UpsertBarrelLayoutParams,
	type ReorderBarrelLayoutParams,
} from "../repositories/BarrelsRepository";
import type { BarrelLayout } from "../models/Barrel";

interface UseBarrelLayoutMutationOptions {
	onAddSuccess?: (layout: BarrelLayout) => void;
	onUpdateSuccess?: (layout: BarrelLayout) => void;
	onReorderSuccess?: () => void;
	onDeleteSuccess?: () => void;
	onError?: (error: Error) => void;
}

export const useBarrelLayoutMutation = ({
	onAddSuccess,
	onUpdateSuccess,
	onReorderSuccess,
	onDeleteSuccess,
	onError,
}: UseBarrelLayoutMutationOptions = {}) => {
	const queryClient = useQueryClient();

	const addMutation = useMutation({
		mutationFn: (params: UpsertBarrelLayoutParams) =>
			upsertBarrelLayout(params),
		onSuccess: (layout) => {
			queryClient.invalidateQueries({ queryKey: ["barrels"] });
			onAddSuccess?.(layout);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	const updateMutation = useMutation({
		mutationFn: (params: UpsertBarrelLayoutParams) =>
			upsertBarrelLayout(params),
		onSuccess: (layout) => {
			queryClient.invalidateQueries({ queryKey: ["barrels"] });
			onUpdateSuccess?.(layout);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	const reorderMutation = useMutation({
		mutationFn: (params: ReorderBarrelLayoutParams) =>
			reorderBarrelLayout(params),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["barrels"] });
			onReorderSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (layoutId: number) => deleteBarrelLayout(layoutId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["barrels"] });
			onDeleteSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		addLayout: addMutation.mutate,
		updateLayout: updateMutation.mutate,
		reorderLayout: reorderMutation.mutate,
		deleteLayout: deleteMutation.mutate,
		isAddingLayout: addMutation.isPending,
		isUpdatingLayout: updateMutation.isPending,
		isReorderingLayout: reorderMutation.isPending,
		isDeletingLayout: deleteMutation.isPending,
	};
};
